// Smart Daara - Physical school: class report cards (validation workflow + PDFs)
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { physicalAPI, gradesAPI, downloadBlob } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { FileDown, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const DIRECTION = ['school_admin', 'manager'];
const DECISIONS = ['Félicitations', 'Encouragements', "Tableau d'honneur", 'Avertissement', 'Blâme'];

export default function ClassReports() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const fr = language === 'fr';
  const isDirection = DIRECTION.includes(user?.role);
  const [classes, setClasses] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [sel, setSel] = useState({ class_id: '', period_id: '' });
  const [summary, setSummary] = useState(null);
  const [edits, setEdits] = useState({}); // {studentId: {general_appreciation, decision}}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const c = (await physicalAPI.listClasses()).data;
      setClasses(c);
      const years = (await physicalAPI.listYears()).data;
      const active = years.find((y) => y.is_active);
      if (active) {
        const p = (await physicalAPI.listPeriods(active.id)).data;
        setPeriods(p);
        if (c.length && p.length) setSel({ class_id: c[0].id, period_id: p[0].id });
      }
    })().catch(() => {});
  }, []);

  const load = async () => {
    if (!sel.class_id || !sel.period_id) return;
    try {
      const s = (await gradesAPI.classSummary(sel.class_id, sel.period_id)).data;
      setSummary(s);
      const e = {};
      s.students.forEach((st) => { e[st.student_user_id] = { general_appreciation: st.general_appreciation, decision: st.decision }; });
      setEdits(e);
    } catch (err) { setSummary(null); }
  };
  useEffect(() => { load(); }, [sel]);

  const isMainTeacher = summary?.class?.main_teacher_id === user?.id;
  const canEdit = isDirection || isMainTeacher;
  const status = summary?.students?.[0]?.status || 'draft';

  const saveStudent = async (sid) => {
    try {
      await gradesAPI.setReportCard({ class_id: sel.class_id, period_id: sel.period_id,
        student_user_id: sid, ...edits[sid] });
      toast.success(fr ? 'Enregistré' : 'Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await gradesAPI.submitReports(sel.class_id, sel.period_id);
      toast.success(`${r.data.submitted} ${fr ? 'bulletins soumis' : 'reports submitted'}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const r = await gradesAPI.publishReports(sel.class_id, sel.period_id);
      toast.success(`${r.data.published} ${fr ? 'bulletins publiés — élèves et parents notifiés' : 'reports published'}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  const dlStudent = async (st) => {
    try {
      const res = await gradesAPI.reportCardPdf(sel.class_id, sel.period_id, st.student_user_id, language);
      downloadBlob(res.data, `bulletin_${st.name.replace(/ /g, '_')}.pdf`);
    } catch (e) { toast.error(fr ? 'Téléchargement impossible' : 'Download failed'); }
  };

  const dlClass = async () => {
    setBusy(true);
    try {
      const res = await gradesAPI.classReportPdf(sel.class_id, sel.period_id, language);
      downloadBlob(res.data, 'bulletins_classe.pdf');
    } catch (e) { toast.error(fr ? 'Téléchargement impossible' : 'Download failed'); }
    finally { setBusy(false); }
  };

  const statusBadge = { draft: ['bg-slate-100 text-slate-600', fr ? 'Brouillon' : 'Draft'],
    submitted: ['bg-amber-100 text-amber-700', fr ? 'Soumis' : 'Submitted'],
    published: ['bg-emerald-100 text-emerald-700', fr ? 'Publié' : 'Published'] };

  return (
    <div className="space-y-5" data-testid="class-reports-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-outfit font-bold text-slate-900">{fr ? 'Bulletins' : 'Report cards'}</h1>
        <div className="flex flex-wrap gap-2">
          {canEdit && status !== 'published' && (
            <Button variant="outline" disabled={busy} onClick={submit} data-testid="submit-reports-btn">
              <Send className="w-4 h-4 mr-2" />{fr ? 'Soumettre la classe' : 'Submit class'}
            </Button>
          )}
          {isDirection && (status === 'submitted' || status === 'published') && (
            <Button className="bg-emerald-700" disabled={busy} onClick={publish} data-testid="publish-reports-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" />{status === 'published' ? (fr ? 'Republier (v+1)' : 'Republish (v+1)') : (fr ? 'Valider & publier' : 'Validate & publish')}
            </Button>
          )}
          <Button variant="outline" disabled={busy || !summary} onClick={dlClass} data-testid="class-pdf-btn">
            <FileDown className="w-4 h-4 mr-2" />{fr ? 'PDF classe' : 'Class PDF'}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div>
          <Label className="text-xs">{fr ? 'Classe' : 'Class'}</Label>
          <select className="h-10 border border-slate-200 rounded-md px-2 text-sm block" value={sel.class_id}
            onChange={(e) => setSel({ ...sel, class_id: e.target.value })} data-testid="reports-class-select">
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">{fr ? 'Période' : 'Period'}</Label>
          <select className="h-10 border border-slate-200 rounded-md px-2 text-sm block" value={sel.period_id}
            onChange={(e) => setSel({ ...sel, period_id: e.target.value })} data-testid="reports-period-select">
            {periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        {summary && (
          <div className="self-end pb-1 flex items-center gap-3 text-sm text-slate-500">
            <Badge className={statusBadge[status][0]}>{statusBadge[status][1]}</Badge>
            <span>{fr ? 'Moy. classe' : 'Class avg'} : <b>{summary.class_avg_general ?? '—'}</b></span>
            <span>{fr ? 'Prof principal' : 'Main teacher'} : {summary.class.main_teacher || '—'}</span>
          </div>
        )}
      </div>

      {summary && (
        <Card className="border-0 shadow-sm overflow-x-auto">
          <CardContent className="p-0">
            <table className="w-full text-sm min-w-[800px]">
              <thead>
                <tr className="bg-slate-50 text-left text-slate-500">
                  <th className="p-3">{fr ? 'Élève' : 'Student'}</th>
                  <th className="p-2">{fr ? 'Moy. gén.' : 'Overall avg'}</th>
                  <th className="p-2">{fr ? 'Rang' : 'Rank'}</th>
                  <th className="p-2">Mention</th>
                  <th className="p-2">{fr ? 'Décision' : 'Decision'}</th>
                  <th className="p-2">{fr ? 'Appréciation générale' : 'General remark'}</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {summary.students.map((st) => (
                  <tr key={st.student_user_id} className="border-t border-slate-100">
                    <td className="p-3 font-medium whitespace-nowrap">{st.name}<br /><span className="text-[10px] text-slate-400">{st.matricule}</span></td>
                    <td className="p-2 font-bold text-emerald-700">{st.general_avg ?? '—'}</td>
                    <td className="p-2">{st.rank ?? '—'} / {summary.effectif}</td>
                    <td className="p-2">{st.mention || '—'}</td>
                    <td className="p-1">
                      {canEdit ? (
                        <select className="h-8 border border-slate-200 rounded px-1 text-xs"
                          value={edits[st.student_user_id]?.decision || ''}
                          onChange={(e) => setEdits({ ...edits, [st.student_user_id]: { ...edits[st.student_user_id], decision: e.target.value } })}
                          onBlur={() => saveStudent(st.student_user_id)}>
                          <option value="">—</option>
                          {DECISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                      ) : (st.decision || '—')}
                    </td>
                    <td className="p-1">
                      {canEdit ? (
                        <Input className="h-8 min-w-[180px] text-xs"
                          value={edits[st.student_user_id]?.general_appreciation || ''}
                          onChange={(e) => setEdits({ ...edits, [st.student_user_id]: { ...edits[st.student_user_id], general_appreciation: e.target.value } })}
                          onBlur={() => saveStudent(st.student_user_id)}
                          data-testid={`general-app-${st.student_user_id}`} />
                      ) : (st.general_appreciation || '—')}
                    </td>
                    <td className="p-2">
                      <Button size="sm" variant="outline" onClick={() => dlStudent(st)} data-testid={`student-pdf-${st.student_user_id}`}>
                        <FileDown className="w-3 h-3 mr-1" />PDF
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
      {!summary && <p className="text-slate-400 text-sm text-center py-8">{fr ? 'Sélectionnez une classe et une période' : 'Select a class and period'}</p>}
    </div>
  );
}
